import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import sessionGuardCopy from '@/i18n/ns/session-guard';
import type { CurrentIdentity } from '@/lib/session-guard';
import { EntryButton, EntryHeading, EntryShell } from './entry-shell';

/**
 * Replaces the whole workspace when the browser's session now belongs to another account, so one
 * company's data is never shown under another's labels (DES1 S3-04/S3-05). Reload is primary; the
 * secondary signs this browser out so the previous account can sign in again.
 */
export function SessionChangedNotice(props: {
  current: CurrentIdentity;
  /** The account this tab was signed in as, for the secondary action. */
  previous: string | null;
  onReload: () => void;
  onSignInAsPrevious: () => void;
}) {
  const copy = useNamespaceCopy(sessionGuardCopy);
  const { current } = props;
  const title =
    current.userId === null
      ? copy.titleSignedOut
      : current.account && current.company
        ? formatCopy(copy.title, { account: current.account, company: current.company })
        : current.account
          ? formatCopy(copy.titleNoCompany, { account: current.account })
          : copy.titleSomeone;
  return (
    // The title names an account and a company: the wider column sets it in two lines on a desktop.
    <EntryShell layer="auth" width="welcome" testId="session-changed">
      <div role="alert" aria-live="assertive">
        <EntryHeading title={title} lead={current.userId === null ? undefined : copy.lead} />
      </div>
      <div className="mx-auto mt-6 grid w-full max-w-[360px] gap-2">
        <EntryButton block onClick={props.onReload}>
          {copy.reload}
        </EntryButton>
        <EntryButton variant="quiet" block onClick={props.onSignInAsPrevious}>
          {props.previous
            ? formatCopy(copy.signInAs, { previous: props.previous })
            : copy.signInAsPrevious}
        </EntryButton>
      </div>
    </EntryShell>
  );
}
